import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Flex, Box, Heading, Text, Button, Select, TextField, Divider, LoadingOrbit } from '@chatsdet/ui';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

type PropertyDetail = { id: string; name: string; baseUrl: string };
type CredItem = { id: string; name: string; authProvider: string; isDefault: boolean };
type StorageStateMetadata = {
  hasStorageState: boolean;
  s3Key: string | null;
  expiresAt: string | null;
  updatedAt: string | null;
};

export const StorageStateDemo: React.FC = () => {
  // Fetch properties and credentials
  const propertiesQ = useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<{ items: PropertyDetail[] }> => {
      const r = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch properties');
      return r.json();
    },
  });

  const [selectedPropertyId, setSelectedPropertyId] = React.useState<string | null>(null);

  const credsQ = useQuery({
    queryKey: ['property-creds', selectedPropertyId],
    queryFn: async (): Promise<{ items: CredItem[] }> => {
      if (!selectedPropertyId) return { items: [] };
      const r = await fetch(`${API_URL}/org/properties/${selectedPropertyId}/credentials`, {
        credentials: 'include',
      });
      if (!r.ok) throw await toApiError(r, 'Failed to fetch credentials');
      return r.json();
    },
    enabled: !!selectedPropertyId,
  });

  // Set default property
  React.useEffect(() => {
    if (propertiesQ.data?.items?.length && !selectedPropertyId) {
      setSelectedPropertyId(propertiesQ.data.items[0]!.id);
    }
  }, [propertiesQ.data, selectedPropertyId]);

  // Left column: Login & Save
  const [selectedCredId, setSelectedCredId] = React.useState<string | null>(null);
  const [loginLogs, setLoginLogs] = React.useState<Array<{ message: string; type: string }>>([]);
  const [loginSuccess, setLoginSuccess] = React.useState<{
    s3Key: string;
    expiresAt: string;
  } | null>(null);

  // Get selected credential details
  const selectedCred = React.useMemo(
    () => credsQ.data?.items.find((c) => c.id === selectedCredId),
    [credsQ.data, selectedCredId]
  );

  // Set default credential
  React.useEffect(() => {
    if (credsQ.data?.items?.length) {
      const def = credsQ.data.items.find((c) => c.isDefault) ?? credsQ.data.items[0]!;
      setSelectedCredId(def.id);
    }
  }, [credsQ.data]);

  const loginAndSave = useMutation({
    mutationFn: async () => {
      if (!selectedCredId || !selectedPropertyId) {
        throw new Error('Missing credential or property');
      }

      setLoginLogs([]);
      setLoginSuccess(null);

      // Use fetch for POST, then handle SSE stream
      const response = await fetch(`${API_URL}/demo/login-and-save`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          credentialId: selectedCredId,
          propertyId: selectedPropertyId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start login');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      return new Promise((resolve, reject) => {
        const processChunk = async () => {
          try {
            const { done, value } = await reader.read();
            if (done) return;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (!line.trim()) continue;

              const [eventLine, dataLine] = line.split('\n');
              if (!eventLine || !dataLine) continue;

              const event = eventLine.replace('event: ', '');
              const dataStr = dataLine.replace('data: ', '');

              try {
                const data = JSON.parse(dataStr);

                if (event === 'log') {
                  setLoginLogs((prev) => [...prev, { message: data.message, type: 'log' }]);
                } else if (event === 'login-event') {
                  if (data.type === 'log') {
                    setLoginLogs((prev) => [
                      ...prev,
                      { message: data.data.message, type: 'login' },
                    ]);
                  }
                } else if (event === 'success') {
                  setLoginSuccess({ s3Key: data.s3Key, expiresAt: data.expiresAt });
                  setLoginLogs((prev) => [...prev, { message: data.message, type: 'success' }]);
                  resolve(data);
                  return;
                } else if (event === 'error') {
                  setLoginLogs((prev) => [...prev, { message: data.message, type: 'error' }]);
                  reject(new Error(data.message));
                  return;
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', e);
              }
            }

            processChunk();
          } catch (e) {
            reject(e);
          }
        };

        processChunk().catch(reject);
      });
    },
  });

  // Right column: Test Saved State
  const [navigateUrl, setNavigateUrl] = React.useState('');
  const [testResult, setTestResult] = React.useState<{
    screenshot: string;
    currentUrl: string;
    title: string;
  } | null>(null);

  const [selectedTestCredId, setSelectedTestCredId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (credsQ.data?.items?.length) {
      const def = credsQ.data.items.find((c) => c.isDefault) ?? credsQ.data.items[0]!;
      setSelectedTestCredId(def.id);
    }
  }, [credsQ.data]);

  const storageStateMetadataQ = useQuery({
    queryKey: ['storage-state-metadata', selectedTestCredId, selectedPropertyId],
    queryFn: async (): Promise<StorageStateMetadata> => {
      if (!selectedTestCredId || !selectedPropertyId) {
        return { hasStorageState: false, s3Key: null, expiresAt: null, updatedAt: null };
      }
      const r = await fetch(
        `${API_URL}/demo/credentials/${selectedTestCredId}/storage-state?propertyId=${selectedPropertyId}`,
        { credentials: 'include' }
      );
      if (!r.ok) throw new Error('Failed to fetch metadata');
      return r.json();
    },
    enabled: !!selectedTestCredId && !!selectedPropertyId,
  });

  const loadStateAndNavigate = useMutation({
    mutationFn: async () => {
      if (!selectedTestCredId || !selectedPropertyId || !navigateUrl) {
        throw new Error('Missing required fields');
      }

      setTestResult(null);

      const r = await fetch(`${API_URL}/demo/login-with-state`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          credentialId: selectedTestCredId,
          propertyId: selectedPropertyId,
          navigateUrl,
        }),
      });

      const data = await r.json();
      if (!r.ok) throw new Error(data.message);

      setTestResult({
        screenshot: data.screenshot,
        currentUrl: data.currentUrl,
        title: data.title,
      });

      return data;
    },
  });

  return (
    <Flex direction="column" gap="lg" style={{ padding: '24px', maxWidth: '1400px' }}>
      <Heading level={2}>Storage State Demo</Heading>
      <Text>Test MCP storage state persistence by logging in and reusing saved sessions.</Text>

      {/* Property selector */}
      <Box>
        <Select
          label="Application"
          selectedKey={selectedPropertyId || undefined}
          onSelectionChange={(key) => setSelectedPropertyId(String(key))}
          isDisabled={propertiesQ.isLoading}
          items={propertiesQ.data?.items.map((p) => ({ id: p.id, label: p.name })) || []}
        />
      </Box>

      <Divider />

      {/* Two column layout */}
      <Flex direction="row" gap="xl" wrap="wrap">
        {/* Left: Login & Save */}
        <Box style={{ flex: '1', minWidth: '400px' }}>
          <Heading level={3}>1. Save Login State</Heading>
          <Text size="sm" style={{ marginBottom: '16px' }}>
            Login with AI and save the browser session
          </Text>

          <Flex direction="column" gap="md">
            <Select
              label="Credential"
              selectedKey={selectedCredId || undefined}
              onSelectionChange={(key) => setSelectedCredId(String(key))}
              isDisabled={credsQ.isLoading}
              items={
                credsQ.data?.items.map((c) => ({
                  id: c.id,
                  label: `${c.name} (${c.authProvider})`,
                })) || []
              }
            />

            {selectedCred && (
              <Box
                style={{
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  border: '1px solid #e0e0e0',
                }}
              >
                <Text weight="bold" style={{ marginBottom: '8px' }}>
                  Credential Details:
                </Text>
                <Text size="sm">Name: {selectedCred.name}</Text>
                <Text size="sm">Auth Provider: {selectedCred.authProvider}</Text>
                <Text size="sm" style={{ marginTop: '8px', fontStyle: 'italic' }}>
                  Username, password, and login instructions will be loaded from this credential
                </Text>
              </Box>
            )}

            <Button
              onPress={() => loginAndSave.mutate()}
              isDisabled={loginAndSave.isPending || !selectedCredId}
            >
              {loginAndSave.isPending ? 'Logging in...' : 'Login & Save State'}
            </Button>

            {/* Login logs */}
            {loginLogs.length > 0 && (
              <Box
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '12px',
                  borderRadius: '6px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              >
                {loginLogs.map((log, i) => (
                  <div
                    key={i}
                    style={{
                      color:
                        log.type === 'error' ? 'red' : log.type === 'success' ? 'green' : 'black',
                      marginBottom: '4px',
                    }}
                  >
                    {log.message}
                  </div>
                ))}
              </Box>
            )}

            {loginSuccess && (
              <Box
                style={{
                  padding: '12px',
                  backgroundColor: '#e6f7e6',
                  borderRadius: '6px',
                  border: '1px solid #4caf50',
                }}
              >
                <Text weight="bold">✅ Success!</Text>
                <Text size="sm">S3 Key: {loginSuccess.s3Key}</Text>
                <Text size="sm">Expires: {new Date(loginSuccess.expiresAt).toLocaleString()}</Text>
              </Box>
            )}
          </Flex>
        </Box>

        {/* Right: Test Saved State */}
        <Box style={{ flex: '1', minWidth: '400px' }}>
          <Heading level={3}>2. Test Saved State</Heading>
          <Text size="sm" style={{ marginBottom: '16px' }}>
            Load saved session and navigate to a page
          </Text>

          <Flex direction="column" gap="md">
            <Select
              label="Credential"
              selectedKey={selectedTestCredId || undefined}
              onSelectionChange={(key) => setSelectedTestCredId(String(key))}
              isDisabled={credsQ.isLoading}
              items={
                credsQ.data?.items.map((c) => ({
                  id: c.id,
                  label: `${c.name} (${c.authProvider})`,
                })) || []
              }
            />

            {/* Storage state metadata */}
            {storageStateMetadataQ.data && (
              <Box
                style={{
                  padding: '12px',
                  backgroundColor: storageStateMetadataQ.data.hasStorageState
                    ? '#e6f7e6'
                    : '#fff3cd',
                  borderRadius: '6px',
                  border: `1px solid ${storageStateMetadataQ.data.hasStorageState ? '#4caf50' : '#ffc107'}`,
                }}
              >
                <Text weight="bold">
                  {storageStateMetadataQ.data.hasStorageState
                    ? '✅ Storage State Available'
                    : '⚠️ No Storage State'}
                </Text>
                {storageStateMetadataQ.data.hasStorageState && (
                  <>
                    <Text size="sm">
                      Expires:{' '}
                      {storageStateMetadataQ.data.expiresAt
                        ? new Date(storageStateMetadataQ.data.expiresAt).toLocaleString()
                        : 'N/A'}
                    </Text>
                    <Text size="sm">
                      Updated:{' '}
                      {storageStateMetadataQ.data.updatedAt
                        ? new Date(storageStateMetadataQ.data.updatedAt).toLocaleString()
                        : 'N/A'}
                    </Text>
                  </>
                )}
              </Box>
            )}

            <TextField
              label="Navigate To URL"
              value={navigateUrl}
              onChange={setNavigateUrl}
              placeholder="https://example.com/dashboard"
            />

            <Button
              onPress={() => loadStateAndNavigate.mutate()}
              isDisabled={
                loadStateAndNavigate.isPending ||
                !navigateUrl ||
                !selectedTestCredId ||
                !storageStateMetadataQ.data?.hasStorageState
              }
            >
              {loadStateAndNavigate.isPending ? 'Loading...' : 'Load State & Navigate'}
            </Button>

            {loadStateAndNavigate.isPending && (
              <Flex direction="row" gap="sm" align="center">
                <LoadingOrbit size="sm" />
                <Text>Loading storage state and navigating...</Text>
              </Flex>
            )}

            {/* Test result */}
            {testResult && (
              <Flex direction="column" gap="md">
                <Box
                  style={{
                    padding: '12px',
                    backgroundColor: '#e6f7e6',
                    borderRadius: '6px',
                    border: '1px solid #4caf50',
                  }}
                >
                  <Text weight="bold">✅ Success!</Text>
                  <Text size="sm">Current URL: {testResult.currentUrl}</Text>
                  <Text size="sm">Title: {testResult.title}</Text>
                </Box>

                {testResult.screenshot && (
                  <Box>
                    <Text weight="bold" style={{ marginBottom: '8px' }}>
                      Screenshot:
                    </Text>
                    <img
                      src={testResult.screenshot}
                      alt="Navigation screenshot"
                      style={{
                        width: '100%',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </Box>
                )}
              </Flex>
            )}

            {loadStateAndNavigate.isError && (
              <Box
                style={{
                  padding: '12px',
                  backgroundColor: '#ffe6e6',
                  borderRadius: '6px',
                  border: '1px solid #f44336',
                }}
              >
                <Text weight="bold">❌ Error</Text>
                <Text size="sm">{String(loadStateAndNavigate.error)}</Text>
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};
